// src/components/admin/dsat/reports/SatPlanWorkspace.js
//
// Full-page Plan workspace (generate + edit) for BOTH SAT and ACT, rendered
// standalone in its own browser tab. Admins set per-subject targets (SAT: R/W +
// Math, 200–800; ACT: English/Math/Reading/Science, 1–36); the engine steers
// hours toward the larger score gap. Read-first layout: donut + summary, then
// editable Plan Summary / Why This Plan, then required subject columns with
// drag-reorder + overflow menus + chevron accordion (guidance + progress
// history), then "Not Covered in This Plan". Regenerate lives next to inputs.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiLoader, FiX, FiMoreHorizontal, FiRefreshCw, FiChevronDown, FiChevronUp } from "react-icons/fi";
import { auth } from "../../../../firebaseConfig";
import { loadSatBreakdownInputs } from "../../../../utils/sat/satBreakdownData";
import { computeSatBreakdown } from "../../../../utils/sat/satBreakdown";
import { generateSatPlanSkeleton } from "../../../../utils/sat/planEngine";
import { loadActBreakdownInputs } from "../../../../utils/act/actBreakdownData";
import { computeActBreakdown } from "../../../../utils/act/actBreakdown";
import { generateActPlanSkeleton } from "../../../../utils/act/actPlanEngine";
import { generatePlanDraft } from "../../../../utils/sat/satPlanAi";
import {
  confirmAndActivatePlan,
  saveDraftPlan,
  savePlanEdits,
  setActivePlan,
  findStudentDirIdByDiagnosticUid,
  fetchStudentsLite,
  linkDiagnosticToStudent,
  fetchPlansForStudentDir,
  itemProgressPct,
} from "../../../../utils/sat/satPlanData";
import {
  PLAN_TYPES,
  planTypeOf,
  planSubjects,
  computeComposite,
  getPlanSubjectScores,
} from "../../../../utils/plan/planConfig";
import HourDonut, { DonutLegend, SUBJECT_COLORS } from "../../../sat/HourDonut";
import PlanProgressHistory from "../../../sat/PlanProgressHistory";

const ITEM_ACCENT = { done: "#059669", in_progress: "#f59e0b", todo: "#cbd5e1" };
const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function groupBySubject(checklist) {
  const map = {};
  (checklist || []).forEach((it) => {
    if (!map[it.subject]) map[it.subject] = [];
    map[it.subject].push({ ...it });
  });
  return map;
}

function AutoTextarea({ value, onChange, placeholder, minRows = 2, style }) {
  const ref = useRef(null);
  const resize = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    resize();
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={placeholder}
      rows={minRows}
      onChange={(e) => onChange(e.target.value)}
      onInput={resize}
      style={{ ...ui.autoTa, ...style }}
    />
  );
}

export default function SatPlanWorkspace({ mode = "generate", onClose, onSaved, source = {}, plan = null }) {
  const isEdit = mode === "edit";
  const type = isEdit ? planTypeOf(plan) : planTypeOf(source.type);
  const config = PLAN_TYPES[type];

  const [phase, setPhase] = useState(isEdit ? "review" : "input");
  const [loadingMsg, setLoadingMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const [targets, setTargets] = useState({});
  const [hours, setHours] = useState("");
  const [written, setWritten] = useState({ parentSummary: "", reasoning: "" });
  const [subjectItems, setSubjectItems] = useState({});
  const [draftMeta, setDraftMeta] = useState(null);

  // Student-link gate (generate only): a plan is only created for a real,
  // linked Student Directory record.
  const [linkState, setLinkState] = useState({ status: isEdit ? "ok" : "checking", dirId: isEdit ? plan?.studentDirId || "" : "" });
  const [students, setStudents] = useState([]);
  const [studentTerm, setStudentTerm] = useState("");
  const [linking, setLinking] = useState(false);

  // Duplicate-plan guard (generate only): warn if a plan of this type exists.
  const [dupState, setDupState] = useState({ status: "idle", plan: null });
  const [dupAck, setDupAck] = useState(false);

  useEffect(() => {
    if (isEdit) {
      setLinkState({ status: "ok", dirId: plan?.studentDirId || "" });
      return;
    }
    const diagUid = source.studentId;
    if (!diagUid) {
      setLinkState({ status: "unlinked", dirId: "" });
      return;
    }
    let alive = true;
    setLinkState({ status: "checking", dirId: "" });
    findStudentDirIdByDiagnosticUid(diagUid)
      .then(async (dirId) => {
        if (!alive) return;
        if (dirId) {
          setLinkState({ status: "ok", dirId });
        } else {
          setLinkState({ status: "unlinked", dirId: "" });
          try {
            const list = await fetchStudentsLite();
            if (alive) setStudents(list);
          } catch {
            /* ignore */
          }
        }
      })
      .catch(() => alive && setLinkState({ status: "unlinked", dirId: "" }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, plan?.studentDirId, source?.studentId]);

  // Once linked, check for an existing plan of the same type for this student.
  useEffect(() => {
    if (isEdit || linkState.status !== "ok" || !linkState.dirId) {
      setDupState({ status: "idle", plan: null });
      return;
    }
    let alive = true;
    setDupState({ status: "checking", plan: null });
    fetchPlansForStudentDir(linkState.dirId)
      .then((list) => {
        if (!alive) return;
        const sameType = (list || []).filter((p) => (p.type || "SAT") === type);
        const existing = sameType.find((p) => p.status === "active") || sameType[0] || null;
        setDupState({ status: existing ? "exists" : "none", plan: existing });
      })
      .catch(() => alive && setDupState({ status: "none", plan: null }));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, linkState.status, linkState.dirId, type]);

  async function linkTo(studentDirId) {
    if (linking) return;
    setLinking(true);
    setError("");
    try {
      await linkDiagnosticToStudent(studentDirId, source.studentId);
      setLinkState({ status: "ok", dirId: studentDirId });
    } catch (e) {
      setError(e?.message || "Failed to link student.");
    } finally {
      setLinking(false);
    }
  }

  // current score per subject (read-only context)
  const currentBySubject = useMemo(() => {
    if (isEdit && plan) {
      const out = {};
      getPlanSubjectScores(plan).forEach((s) => {
        if (s.current != null) out[s.subject] = s.current;
      });
      return out;
    }
    return source.currentBySubject || {};
  }, [isEdit, plan, source]);

  // Subjects to plan for: the type's sections, narrowed to those with a known
  // current score (so a skipped ACT Science section is omitted automatically).
  const subjects = useMemo(() => {
    const all = planSubjects(type);
    const known = all.filter((s) => num(currentBySubject[s]) != null);
    return known.length ? known : all;
  }, [type, currentBySubject]);

  useEffect(() => {
    setError("");
    setBusy(false);
    if (isEdit && plan) {
      setPhase("review");
      const t = {};
      getPlanSubjectScores(plan).forEach((s) => {
        if (s.target != null) t[s.subject] = String(s.target);
      });
      setTargets(t);
      setHours(plan.totalHours != null ? String(plan.totalHours) : "");
      setWritten({
        parentSummary: plan.writtenPlan?.parentSummary || "",
        reasoning: plan.writtenPlan?.reasoning || "",
      });
      setSubjectItems(groupBySubject(plan.checklist));
      setDraftMeta({ subjects: plan.snapshot?.subjects || [], notTested: plan.snapshot?.notTested || [], aiProvider: plan.aiProvider || null });
    } else {
      setPhase("input");
      setTargets(source.targetBySubject ? { ...source.targetBySubject } : {});
      setHours(source.initialHours != null ? String(source.initialHours) : "20");
      setWritten({ parentSummary: "", reasoning: "" });
      setSubjectItems({});
      setDraftMeta(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, plan?.id, source?.attemptId, source?.testId, source?.studentId]);

  const studentName = isEdit ? plan?.studentName || source.studentName : source.studentName;
  const contextLabel = source.attemptLabel || plan?.attemptLabel || "";

  const targetVals = subjects.map((s) => num(targets[s]));
  const currentVals = subjects.map((s) => num(currentBySubject[s]));
  const totalTarget = computeComposite(type, targetVals.filter((v) => v != null));
  const currentTotal = computeComposite(type, currentVals.filter((v) => v != null));
  const gap = totalTarget != null && currentTotal != null ? Math.max(0, totalTarget - currentTotal) : null;

  const requiredHours = useMemo(() => {
    let sum = 0;
    Object.values(subjectItems).forEach((list) =>
      list.forEach((it) => {
        if (!it.isStretch) sum += num(it.allocatedHours) || 0;
      })
    );
    return Math.round(sum);
  }, [subjectItems]);

  const allocBySubject = subjects.map((s) => ({
    subject: s,
    hours: (subjectItems[s] || []).filter((it) => !it.isStretch).reduce((a, b) => a + (num(b.allocatedHours) || 0), 0),
  }));

  function validateInputs() {
    const h = num(hours);
    if (h == null || h <= 0) return "Enter the number of tutoring hours.";
    for (const s of subjects) {
      const t = num(targets[s]);
      if (t == null) return `Enter a target score for ${s}.`;
      if (t < config.min || t > config.max) return `${s} target must be between ${config.min} and ${config.max}.`;
      const c = num(currentBySubject[s]);
      if (c != null && t < c) return `${s} target can't be below the current score (${c}).`;
    }
    if (currentTotal != null && totalTarget != null && totalTarget <= currentTotal)
      return "Total target must be greater than the current score.";
    return "";
  }

  async function runGenerate() {
    const v = validateInputs();
    if (v) {
      setError(v);
      return;
    }
    const h = num(hours);
    setError("");
    setBusy(true);
    setPhase("loading");
    try {
      setLoadingMsg("Analyzing the diagnostic breakdown…");
      const src = isEdit
        ? { testId: plan.testId, attemptId: plan.sourceAttemptId, studentId: plan.diagnosticStudentId }
        : source;
      const targetBySubject = {};
      subjects.forEach((s) => (targetBySubject[s] = num(targets[s])));

      let skeleton;
      if (type === "ACT") {
        const { questionIndex, answers, scienceSkipped } = await loadActBreakdownInputs({
          testId: src.testId,
          attemptId: src.attemptId,
          studentId: src.studentId,
          answers: src.answers,
        });
        const breakdown = computeActBreakdown(questionIndex, answers, scienceSkipped);
        skeleton = generateActPlanSkeleton(breakdown, h, { currentBySubject, targetBySubject });
      } else {
        const { questionIndex, answers } = await loadSatBreakdownInputs({
          testId: src.testId,
          attemptId: src.attemptId,
          studentId: src.studentId,
          answers: src.answers,
        });
        const breakdown = computeSatBreakdown(questionIndex, answers);
        skeleton = generateSatPlanSkeleton(breakdown, h, {
          currentRw: currentBySubject["Reading & Writing"],
          currentMath: currentBySubject["Math"],
          targetRw: targetBySubject["Reading & Writing"],
          targetMath: targetBySubject["Math"],
        });
      }

      if (!skeleton.checklist.length) {
        setError("No weaknesses were found in this diagnostic, so there is nothing to plan.");
        setPhase(isEdit ? "review" : "input");
        setBusy(false);
        return;
      }
      setLoadingMsg("Writing the plan summary and tutor guidance…");
      const context = {
        studentName: studentName || "",
        testType: type,
        totalHours: h,
        currentScore: currentTotal,
        targetScore: totalTarget,
        scoreGap: skeleton.scoreGap,
        attemptLabel: contextLabel,
        subjects: skeleton.subjects,
      };
      const result = await generatePlanDraft({ skeleton, context });
      setWritten({
        parentSummary: result.writtenPlan?.parentSummary || "",
        reasoning: result.writtenPlan?.reasoning || "",
      });
      setSubjectItems(groupBySubject(result.checklist));
      setDraftMeta({ subjects: result.subjects || [], notTested: result.notTested || [], aiProvider: result.aiProvider, aiError: result.aiError });
      setPhase("review");
    } catch (e) {
      setError(e?.message || "Failed to generate the plan.");
      setPhase(isEdit ? "review" : "input");
    } finally {
      setBusy(false);
    }
  }

  const patchItem = (subject, idx, patch) =>
    setSubjectItems((p) => ({ ...p, [subject]: p[subject].map((it, i) => (i === idx ? { ...it, ...patch } : it)) }));
  const moveWithin = (subject, from, to) =>
    setSubjectItems((p) => {
      const list = [...p[subject]];
      if (to < 0 || to >= list.length || from === to) return p;
      const [m] = list.splice(from, 1);
      list.splice(to, 0, m);
      return { ...p, [subject]: list };
    });
  const removeItem = (subject, idx) =>
    setSubjectItems((p) => ({ ...p, [subject]: p[subject].filter((_, i) => i !== idx) }));

  function buildChecklistForSave() {
    const out = [];
    subjects.forEach((subject) => {
      const list = subjectItems[subject] || [];
      const required = list.filter((it) => !it.isStretch);
      const stretch = list.filter((it) => it.isStretch);
      let rank = 0;
      [...required, ...stretch].forEach((it) => {
        rank += 1;
        out.push({
          id: it.id,
          subject: it.subject,
          majorCategory: it.majorCategory,
          subcategory: it.subcategory,
          key: it.key,
          accuracyPct: it.accuracyPct ?? null,
          total: it.total ?? 0,
          multiplier: it.multiplier ?? 0,
          rxHours: it.rxHours ?? 0,
          allocatedHours: it.isStretch ? 0 : num(it.allocatedHours) || 0,
          isStretch: !!it.isStretch,
          subjectRank: rank,
          priority: out.length + 1,
          guidance: it.guidance || "",
          status: it.status || "todo",
          coveredHours: Number(it.coveredHours) || 0,
          progressPct: Number(it.progressPct) || 0,
          progressHistory: Array.isArray(it.progressHistory) ? it.progressHistory : [],
          completedByUid: it.completedByUid || null,
          completedByName: it.completedByName || null,
          completedAt: it.completedAt || null,
          sessionLogId: it.sessionLogId || null,
        });
      });
    });
    return out;
  }

  async function handleSave(activate) {
    setBusy(true);
    setError("");
    try {
      const checklist = buildChecklistForSave();
      const subjectScores = subjects
        .map((s) => ({ subject: s, current: num(currentBySubject[s]), target: num(targets[s]) }))
        .filter((x) => x.current != null || x.target != null);
      const commonFields = {
        type,
        totalHours: num(hours) || 0,
        currentScore: currentTotal,
        targetScore: totalTarget,
        scoreGap: gap,
        subjectScores,
        writtenPlan: { parentSummary: written.parentSummary || "", reasoning: written.reasoning || "" },
        checklist,
      };
      const user = auth.currentUser;
      const actor = { uid: user?.uid || null, name: user?.displayName || user?.email || null };

      let savedId;
      let resolvedDirId = isEdit ? plan?.studentDirId || "" : "";
      if (isEdit && plan?.id) {
        await savePlanEdits(plan.id, commonFields);
        if (activate && plan.studentDirId) await setActivePlan(plan.studentDirId, plan.id);
        else if (activate) await savePlanEdits(plan.id, { status: "active" });
        savedId = plan.id;
      } else {
        // Generation is gated on a linked student, so dirId is already resolved.
        resolvedDirId = linkState.dirId || (await findStudentDirIdByDiagnosticUid(source.studentId)) || "";
        if (!resolvedDirId) {
          setError("This diagnostic isn't linked to a student. Link a student before creating a plan.");
          setBusy(false);
          return;
        }
        const fullPlan = {
          studentDirId: resolvedDirId,
          diagnosticStudentId: source.studentId || "",
          studentName: studentName || "",
          sourceAttemptId: source.attemptId || "",
          testId: source.testId || "",
          attemptLabel: contextLabel,
          sourceCompletedAtMs: source.sourceCompletedAtMs || null,
          totalScore: num(source.totalScore),
          ...commonFields,
          snapshot: {
            subjects: draftMeta?.subjects || [],
            notTested: draftMeta?.notTested || [],
            summary: {
              requiredCount: checklist.filter((c) => !c.isStretch).length,
              stretchCount: checklist.filter((c) => c.isStretch).length,
              requiredHours,
            },
          },
          aiProvider: draftMeta?.aiProvider || "local",
        };
        savedId = activate ? await confirmAndActivatePlan(fullPlan, actor) : await saveDraftPlan(fullPlan, actor);
      }
      setBusy(false);
      onSaved && onSaved(savedId, resolvedDirId);
    } catch (e) {
      setError(e?.message || "Failed to save the plan.");
      setBusy(false);
    }
  }

  const donutSegs = allocBySubject.map((a) => ({ label: a.subject, value: a.hours, color: SUBJECT_COLORS[a.subject] }));
  const compositeLabel = config.composite === "avg" ? "Composite" : "Total";
  const gateActive = !isEdit && linkState.status !== "ok";
  const dupActive = !isEdit && linkState.status === "ok" && dupState.status === "exists" && !dupAck;

  return (
    <div style={ui.page}>
      <style>{styleTag}</style>

      <header style={ui.topbar}>
        <div>
          <div style={ui.title}>
            {isEdit ? "Edit Plan" : `Generate ${config.label} Plan`}
          </div>
          <div style={ui.sub}>
            {studentName || "Student"}
            {contextLabel ? ` · ${contextLabel}` : ""}
          </div>
        </div>
        {onClose && (
          <button style={ui.closeBtn} onClick={onClose} disabled={busy} aria-label="Close">
            <FiX size={18} />
          </button>
        )}
      </header>

      <div style={ui.scroll}>
        <div style={ui.inner}>
          {error && <div style={ui.error}>{error}</div>}

          {gateActive ? (
            <LinkGate
              studentName={studentName}
              status={linkState.status}
              students={students}
              term={studentTerm}
              setTerm={setStudentTerm}
              linking={linking}
              onLink={linkTo}
            />
          ) : dupActive ? (
            <DupGate
              typeLabel={config.label}
              plan={dupState.plan}
              dirId={linkState.dirId}
              onContinue={() => setDupAck(true)}
            />
          ) : (
            <>
          {phase === "input" && (
            <div style={ui.introCard}>
              <h2 style={ui.introTitle}>New {config.label} Plan</h2>
              <p style={ui.introHelp}>
                Set a target score for each {type === "ACT" ? "section" : "subject"} and the tutoring
                hours. The plan focuses the weakest diagnostic areas first and steers more hours
                toward the {type === "ACT" ? "section" : "subject"} with the bigger score gap.
              </p>
              <div style={ui.subjTargets}>
                {subjects.map((s) => (
                  <SubjectTargetInput
                    key={s}
                    label={s}
                    current={num(currentBySubject[s])}
                    value={targets[s] || ""}
                    onChange={(v) => setTargets((p) => ({ ...p, [s]: v }))}
                  />
                ))}
                <div style={ui.field}>
                  <label style={ui.fieldLabel}>Tutoring hours</label>
                  <input type="number" inputMode="numeric" style={ui.bigInput} value={hours} onChange={(e) => setHours(e.target.value)} placeholder="20" />
                </div>
                <button style={ui.generateBtn} onClick={runGenerate} disabled={busy}>
                  Generate plan
                </button>
              </div>
              {totalTarget != null && (
                <div style={ui.gapNote}>
                  {compositeLabel} target <strong>{totalTarget}</strong>
                  {gap != null ? ` · +${gap} point goal` : ""}
                </div>
              )}
            </div>
          )}

          {phase === "loading" && (
            <div style={ui.loadingWrap}>
              <FiLoader size={30} className="sat-spin" />
              <div style={ui.loadingTitle}>Building the plan…</div>
              <div style={ui.loadingMsg}>{loadingMsg}</div>
            </div>
          )}

          {phase === "review" && (
            <>
              <div style={ui.regenBar}>
                <div style={ui.regenSubjects}>
                  {subjects.map((s) => (
                    <RegenSubject
                      key={s}
                      label={shortLabel(s)}
                      current={num(currentBySubject[s])}
                      value={targets[s] || ""}
                      onChange={(v) => setTargets((p) => ({ ...p, [s]: v }))}
                    />
                  ))}
                  <span style={ui.regenDivider} />
                  <input type="number" style={ui.smallInput} value={hours} onChange={(e) => setHours(e.target.value)} aria-label="Tutoring hours" />
                  <span style={ui.regenUnit}>hours</span>
                  {gap != null && <span style={ui.gapPill}>+{gap}</span>}
                </div>
                <button style={ui.regenBtn} onClick={runGenerate} disabled={busy}>
                  <FiRefreshCw size={13} /> Regenerate
                </button>
              </div>

              <div style={ui.donutCard}>
                <HourDonut segments={donutSegs} />
                <div style={ui.summaryArea}>
                  <DonutLegend segments={donutSegs} />
                  <div style={ui.tiles}>
                    <Tile value={currentTotal != null ? currentTotal : "—"} label="Current" />
                    <Tile value={totalTarget != null ? totalTarget : "—"} label="Target" />
                    <Tile value={gap != null ? `+${gap}` : "—"} label="Gap" accent />
                    <Tile value={`${requiredHours}h`} label="Planned" />
                  </div>
                </div>
              </div>

              {draftMeta?.aiProvider === "local" && (
                <div style={ui.notice}>
                  AI draft unavailable — showing an editable template. {draftMeta?.aiError ? `(${draftMeta.aiError})` : ""}
                </div>
              )}

              <div style={ui.proseCard}>
                <div style={ui.proseLabel}>Plan Summary</div>
                <AutoTextarea value={written.parentSummary} onChange={(v) => setWritten((w) => ({ ...w, parentSummary: v }))} placeholder="Plan summary…" />
                <div style={{ ...ui.proseLabel, marginTop: 18 }}>Why This Plan</div>
                <AutoTextarea value={written.reasoning} onChange={(v) => setWritten((w) => ({ ...w, reasoning: v }))} placeholder="Why this plan…" />
              </div>

              <div style={ui.subjectsGrid}>
                {subjects
                  .filter((s) => (subjectItems[s] || []).some((it) => !it.isStretch))
                  .map((subject) => (
                    <SubjectColumn
                      key={subject}
                      subject={subject}
                      items={subjectItems[subject] || []}
                      onPatch={(idx, patch) => patchItem(subject, idx, patch)}
                      onMove={(from, to) => moveWithin(subject, from, to)}
                      onRemove={(idx) => removeItem(subject, idx)}
                    />
                  ))}
              </div>

              <NotCoveredSection subjects={subjects} subjectItems={subjectItems} onPatch={patchItem} onRemove={removeItem} />
            </>
          )}
            </>
          )}
        </div>
      </div>

      {!gateActive && !dupActive && phase !== "loading" && (
        <footer style={ui.footer}>
          {phase === "input" ? (
            <button style={ui.ghost} onClick={onClose} disabled={busy}>
              Cancel
            </button>
          ) : (
            <>
              <button style={ui.ghost} onClick={() => handleSave(false)} disabled={busy}>
                {isEdit ? "Save changes" : "Save as draft"}
              </button>
              <button style={ui.primary} onClick={() => handleSave(true)} disabled={busy}>
                {busy ? "Saving…" : isEdit ? "Save & set active" : "Confirm & activate"}
              </button>
            </>
          )}
        </footer>
      )}
    </div>
  );
}

function shortLabel(s) {
  return s === "Reading & Writing" ? "R/W" : s;
}

function SubjectTargetInput({ label, current, value, onChange }) {
  return (
    <div style={ui.field}>
      <label style={ui.fieldLabel}>{label} target</label>
      <div style={ui.subjInputRow}>
        <span style={ui.curScore}>{current != null ? current : "—"}</span>
        <span style={ui.arrowSmall}>→</span>
        <input type="number" inputMode="numeric" style={ui.bigInput} value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function RegenSubject({ label, current, value, onChange }) {
  return (
    <span style={ui.regenSubj}>
      <span style={ui.regenSubjLabel}>{label}</span>
      <span style={ui.regenCur}>{current != null ? current : "—"}</span>
      <span style={ui.regenArrow}>→</span>
      <input type="number" style={ui.smallInput} value={value} onChange={(e) => onChange(e.target.value)} aria-label={`${label} target`} />
    </span>
  );
}

function Tile({ value, label, accent }) {
  return (
    <div style={ui.tile}>
      <div style={{ ...ui.tileVal, ...(accent ? { color: "#16a34a" } : null) }}>{value}</div>
      <div style={ui.tileLabel}>{label}</div>
    </div>
  );
}

function SubjectColumn({ subject, items, onPatch, onMove, onRemove }) {
  const [dragFrom, setDragFrom] = useState(null);
  const required = items.map((it, i) => ({ it, i })).filter(({ it }) => !it.isStretch);
  const reqHours = required.reduce((a, b) => a + (num(b.it.allocatedHours) || 0), 0);
  const handleDrop = (toIndex) => {
    if (dragFrom == null) return;
    onMove(dragFrom, toIndex);
    setDragFrom(null);
  };
  return (
    <section style={ui.col}>
      <div style={ui.colHead}>
        <span style={ui.colTitle}>{subject}</span>
        <span style={ui.colMeta}>
          {required.length} focus · {Math.round(reqHours)}h
        </span>
      </div>
      {required.length === 0 && <div style={ui.emptyCol}>No required items.</div>}
      {required.map(({ it, i }, displayIdx) => (
        <ItemRow
          key={it.id}
          it={it}
          rank={displayIdx + 1}
          draggable
          onDragStart={() => setDragFrom(i)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => handleDrop(i)}
          onPatch={(patch) => onPatch(i, patch)}
          onRemove={() => onRemove(i)}
          onToggle={() => onPatch(i, { isStretch: true })}
          toggleLabel="Move to Not Covered"
        />
      ))}
    </section>
  );
}

function NotCoveredSection({ subjects, subjectItems, onPatch, onRemove }) {
  const groups = subjects
    .map((subject) => ({
      subject,
      entries: (subjectItems[subject] || []).map((it, i) => ({ it, i })).filter(({ it }) => it.isStretch),
    }))
    .filter((g) => g.entries.length);
  if (!groups.length) return null;
  return (
    <section style={ui.notCovered}>
      <div style={ui.notCoveredHead}>
        <span style={ui.notCoveredTitle}>Not Covered in This Plan</span>
        <span style={ui.notCoveredSub}>Still diagnostic weaknesses, but not included because the planned hours are limited.</span>
      </div>
      <div style={ui.notCoveredGrid}>
        {groups.map((g) => (
          <div key={g.subject}>
            <div style={ui.ncSubject}>{g.subject}</div>
            {g.entries.map(({ it, i }) => (
              <ItemRow
                key={it.id}
                it={it}
                dim
                onPatch={(patch) => onPatch(g.subject, i, patch)}
                onRemove={() => onRemove(g.subject, i)}
                onToggle={() => onPatch(g.subject, i, { isStretch: false })}
                toggleLabel="Add to plan"
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function ItemRow({ it, rank, draggable, onDragStart, onDragOver, onDrop, onPatch, onRemove, onToggle, toggleLabel, dim }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [editingHours, setEditingHours] = useState(false);
  const [open, setOpen] = useState(false);

  const pct = itemProgressPct(it);
  const status = pct >= 100 ? "done" : pct > 0 ? "in_progress" : "todo";
  const hasProgress = pct > 0 || (Number(it.coveredHours) || 0) > 0;
  const done = status === "done";

  return (
    <div
      style={{ ...ui.row, ...(dim ? ui.rowDim : null) }}
      draggable={draggable || undefined}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div style={ui.rowHeader}>
        {draggable && (
          <span style={ui.dragHandle} title="Drag to reorder">
            ⠿
          </span>
        )}
        {rank != null && <span style={ui.rank}>{rank}</span>}
        <div style={ui.rowTitleWrap}>
          <div style={{ ...ui.rowTitle, ...(done ? { color: "#94a3b8", textDecoration: "line-through" } : null) }}>
            {it.subcategory}
          </div>
          <div style={ui.rowSub}>
            {it.majorCategory}
            {it.accuracyPct != null ? ` · ${it.accuracyPct}%` : ""}
          </div>
        </div>

        {!it.isStretch &&
          (editingHours ? (
            <input
              type="number"
              min="0"
              step="1"
              autoFocus
              style={ui.hoursInput}
              value={it.allocatedHours}
              onChange={(e) => onPatch({ allocatedHours: e.target.value })}
              onBlur={() => setEditingHours(false)}
            />
          ) : (
            <button style={ui.hoursBadge} onClick={() => setEditingHours(true)} title="Edit hours">
              {it.allocatedHours}h
            </button>
          ))}

        <button style={ui.iconBtn} onClick={() => setOpen((o) => !o)} aria-label="Details">
          {open ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
        </button>

        <div style={{ position: "relative" }}>
          <button style={ui.iconBtn} onClick={() => setMenuOpen((o) => !o)} aria-label="Item actions">
            <FiMoreHorizontal size={15} />
          </button>
          {menuOpen && (
            <>
              <div style={ui.menuBackdrop} onClick={() => setMenuOpen(false)} />
              <div style={ui.menu}>
                <button style={ui.menuItem} onClick={() => { onToggle(); setMenuOpen(false); }}>
                  {toggleLabel}
                </button>
                <button style={{ ...ui.menuItem, color: "#b91c1c" }} onClick={() => { onRemove(); setMenuOpen(false); }}>
                  Remove from plan
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {!dim && hasProgress && (
        <div style={ui.rowProgress}>
          <div style={ui.rowProgBarOuter}>
            <div style={{ ...ui.rowProgBarInner, width: `${pct}%`, background: ITEM_ACCENT[status] }} />
          </div>
          <span style={ui.rowProgPct}>
            {Number(it.coveredHours) || 0}/{it.allocatedHours}h · {pct}%
          </span>
        </div>
      )}

      {open && (
        <div style={ui.details}>
          <AutoTextarea value={it.guidance || ""} onChange={(v) => onPatch({ guidance: v })} placeholder="Tutor guidance…" style={ui.rowGuidance} />
          {!dim && <PlanProgressHistory item={it} guidance={false} />}
        </div>
      )}
    </div>
  );
}

function LinkGate({ studentName, status, students, term, setTerm, linking, onLink }) {
  if (status === "checking") {
    return (
      <div style={ui.loadingWrap}>
        <FiLoader size={28} className="sat-spin" />
        <div style={ui.loadingTitle}>Checking student link…</div>
      </div>
    );
  }
  const t = term.trim().toLowerCase();
  const filtered = (students || [])
    .filter((s) => !t || (s.name || "").toLowerCase().includes(t))
    .slice(0, 40);
  return (
    <div style={ui.introCard}>
      <h2 style={ui.introTitle}>Link a student first</h2>
      <p style={ui.introHelp}>
        This diagnostic account{studentName ? ` (“${studentName}”)` : ""} isn’t linked to a student in the
        Directory. Plans are only created for enrolled students — search and link the correct student
        record to continue.
      </p>
      <input
        style={ui.searchInput}
        placeholder="Search students by name…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        autoFocus
      />
      <div style={ui.studentList}>
        {filtered.length === 0 ? (
          <div style={ui.emptyCol}>No matching students found.</div>
        ) : (
          filtered.map((s) => (
            <div key={s.id} style={ui.studentRow}>
              <div style={{ minWidth: 0 }}>
                <div style={ui.studentName}>{s.name || "(no name)"}</div>
                <div style={ui.studentMeta}>
                  {s.grade ? `Grade ${s.grade}` : ""}
                  {s.diagnosticUid ? `${s.grade ? " · " : ""}already linked to a diagnostic` : ""}
                </div>
              </div>
              <button style={ui.linkBtn} disabled={linking} onClick={() => onLink(s.id)}>
                {linking ? "Linking…" : "Link"}
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DupGate({ typeLabel, plan, dirId, onContinue }) {
  const openExisting = () => {
    const q = plan?.id ? `?plan=${encodeURIComponent(plan.id)}` : "";
    if (dirId) window.location.href = `/students/directory/${encodeURIComponent(dirId)}/plan${q}`;
  };
  const statusWord = plan?.status === "active" ? "active" : "existing";
  return (
    <div style={ui.introCard}>
      <h2 style={ui.introTitle}>This student already has a {typeLabel} plan</h2>
      <p style={ui.introHelp}>
        There’s already an {statusWord} {typeLabel} plan for this student
        {plan?.totalHours ? ` (${plan.totalHours}h` : ""}
        {plan?.targetScore != null ? `, target ${plan.targetScore})` : plan?.totalHours ? ")" : ""}. Open the
        existing plan instead of creating a duplicate, or continue only if you intend to make a new one.
      </p>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button style={ui.primary} onClick={openExisting}>
          Open existing plan
        </button>
        <button style={ui.ghost} onClick={onContinue}>
          Generate another anyway
        </button>
      </div>
    </div>
  );
}

const styleTag = ".sat-spin { animation: satspin 0.8s linear infinite; } @keyframes satspin { to { transform: rotate(360deg); } }";

const ui = {
  page: { position: "fixed", inset: 0, background: "#f6f7f9", display: "flex", flexDirection: "column", zIndex: 1 },
  topbar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 28px", background: "#fff", borderBottom: "1px solid #eaecef" },
  title: { fontSize: 18, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.01em" },
  sub: { fontSize: 12.5, color: "#64748b", marginTop: 3 },
  closeBtn: { border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, width: 36, height: 36, cursor: "pointer", color: "#475569", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  scroll: { flex: 1, overflowY: "auto" },
  inner: { maxWidth: 1120, margin: "0 auto", padding: "28px 28px 48px" },
  error: { background: "#fef2f2", border: "1px solid #fca5a5", color: "#b91c1c", padding: "10px 14px", borderRadius: 10, fontSize: 13, marginBottom: 16 },

  introCard: { background: "#fff", border: "1px solid #eaecef", borderRadius: 16, padding: 32, maxWidth: 920, margin: "24px auto 0" },
  introTitle: { fontSize: 22, fontWeight: 800, color: "#0f172a", margin: "0 0 8px" },
  introHelp: { fontSize: 14, color: "#64748b", lineHeight: 1.6, margin: "0 0 26px", maxWidth: 660 },
  subjTargets: { display: "flex", alignItems: "flex-end", gap: 20, flexWrap: "wrap" },
  field: {},
  fieldLabel: { display: "block", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280", marginBottom: 6 },
  subjInputRow: { display: "flex", alignItems: "center", gap: 8 },
  curScore: { fontSize: 18, fontWeight: 700, color: "#94a3b8" },
  arrowSmall: { color: "#cbd5e1" },
  bigInput: { width: 96, padding: "11px 13px", border: "1px solid #d1d5db", borderRadius: 12, fontSize: 20, fontWeight: 700, color: "#0f172a", boxSizing: "border-box" },
  generateBtn: { border: "none", background: "#0f172a", color: "#fff", padding: "13px 22px", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 700 },
  gapNote: { fontSize: 13, color: "#16a34a", fontWeight: 600, marginTop: 18 },

  searchInput: { width: "100%", maxWidth: 420, padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 10, fontSize: 14, boxSizing: "border-box", marginBottom: 12 },
  studentList: { border: "1px solid #eef2f7", borderRadius: 12, maxHeight: 360, overflowY: "auto" },
  studentRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "11px 14px", borderBottom: "1px solid #f1f5f9" },
  studentName: { fontSize: 14, fontWeight: 700, color: "#0f172a" },
  studentMeta: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
  linkBtn: { border: "none", background: "#0f172a", color: "#fff", padding: "7px 16px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", flexShrink: 0 },

  loadingWrap: { textAlign: "center", padding: "90px 0", color: "#334155" },
  loadingTitle: { fontSize: 17, fontWeight: 700, marginTop: 18, color: "#0f172a" },
  loadingMsg: { fontSize: 13.5, color: "#64748b", marginTop: 6 },

  regenBar: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, background: "#fff", border: "1px solid #eaecef", borderRadius: 12, padding: "10px 16px", marginBottom: 16, flexWrap: "wrap" },
  regenSubjects: { display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" },
  regenSubj: { display: "inline-flex", alignItems: "center", gap: 5 },
  regenSubjLabel: { fontSize: 11, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" },
  regenCur: { fontSize: 13, fontWeight: 700, color: "#94a3b8" },
  regenArrow: { color: "#cbd5e1" },
  smallInput: { width: 60, padding: "6px 8px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, fontWeight: 700, color: "#0f172a" },
  regenDivider: { width: 1, height: 22, background: "#e5e7eb", margin: "0 2px" },
  regenUnit: { fontSize: 13, color: "#64748b" },
  gapPill: { fontSize: 12, fontWeight: 700, background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 999 },
  regenBtn: { display: "inline-flex", alignItems: "center", gap: 6, border: "1px solid #d1d5db", background: "#fff", color: "#0f172a", padding: "8px 14px", borderRadius: 9, cursor: "pointer", fontSize: 13, fontWeight: 700 },

  donutCard: { background: "#fff", border: "1px solid #eaecef", borderRadius: 16, padding: "20px 24px", marginBottom: 16, display: "flex", alignItems: "center", gap: 32, flexWrap: "wrap" },
  summaryArea: { display: "flex", flexDirection: "column", gap: 14, flex: 1, minWidth: 260 },
  tiles: { display: "grid", gridTemplateColumns: "repeat(4, minmax(64px, 1fr))", gap: 10 },
  tile: { background: "#f8fafc", border: "1px solid #eef2f7", borderRadius: 10, padding: "9px 11px" },
  tileVal: { fontSize: 18, fontWeight: 800, color: "#0f172a", lineHeight: 1 },
  tileLabel: { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "#94a3b8", marginTop: 5 },

  notice: { background: "#fffbeb", border: "1px solid #fcd34d", color: "#92400e", padding: "9px 12px", borderRadius: 10, fontSize: 12.5, marginBottom: 14 },

  proseCard: { background: "#fff", border: "1px solid #eaecef", borderRadius: 16, padding: 24, marginBottom: 20 },
  proseLabel: { fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "#94a3b8", marginBottom: 8 },
  autoTa: { width: "100%", border: "1px solid transparent", borderRadius: 10, padding: "10px 12px", fontSize: 15, lineHeight: 1.65, color: "#1e293b", boxSizing: "border-box", resize: "none", overflow: "hidden", fontFamily: "inherit", background: "#f8fafc" },

  subjectsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 20 },
  col: { background: "#fff", border: "1px solid #eaecef", borderRadius: 16, padding: 20 },
  colHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 14 },
  colTitle: { fontSize: 15, fontWeight: 800, color: "#0f172a", textTransform: "uppercase", letterSpacing: "0.05em" },
  colMeta: { fontSize: 12, color: "#94a3b8", fontWeight: 600 },

  emptyCol: { fontSize: 12.5, color: "#94a3b8", padding: "6px 0" },
  row: { padding: "12px 0", borderTop: "1px solid #f1f5f9" },
  rowDim: { opacity: 0.7 },
  rowHeader: { display: "flex", alignItems: "flex-start", gap: 8 },
  dragHandle: { cursor: "grab", color: "#cbd5e1", fontSize: 16, lineHeight: "20px", userSelect: "none", paddingTop: 1 },
  rank: { fontSize: 11, fontWeight: 800, background: "#0f172a", color: "#fff", borderRadius: 999, minWidth: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 },
  rowTitleWrap: { flex: 1, minWidth: 0 },
  rowTitle: { fontSize: 14, fontWeight: 700, color: "#0f172a" },
  rowSub: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
  hoursBadge: { border: "1px solid #e5e7eb", background: "#f8fafc", color: "#0f172a", borderRadius: 8, padding: "3px 9px", fontSize: 13, fontWeight: 700, cursor: "pointer" },
  hoursInput: { width: 54, padding: "4px 6px", border: "1px solid #94a3b8", borderRadius: 8, fontSize: 13, fontWeight: 700 },
  iconBtn: { border: "none", background: "transparent", color: "#94a3b8", cursor: "pointer", padding: 4, borderRadius: 6, display: "inline-flex" },
  menuBackdrop: { position: "fixed", inset: 0, zIndex: 5 },
  menu: { position: "absolute", top: "100%", right: 0, marginTop: 4, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, boxShadow: "0 12px 28px rgba(0,0,0,0.14)", zIndex: 6, minWidth: 200, overflow: "hidden" },
  menuItem: { display: "block", width: "100%", textAlign: "left", border: "none", background: "#fff", padding: "10px 14px", fontSize: 13, color: "#334155", cursor: "pointer" },
  rowProgress: { display: "flex", alignItems: "center", gap: 10, marginTop: 8, paddingLeft: 28 },
  rowProgBarOuter: { flex: 1, height: 8, background: "#e2e8f0", borderRadius: 999, overflow: "hidden" },
  rowProgBarInner: { height: "100%", borderRadius: 999 },
  rowProgPct: { fontSize: 11.5, fontWeight: 700, color: "#64748b", flexShrink: 0 },
  details: { marginTop: 10, paddingLeft: 28 },
  rowGuidance: { fontSize: 13, background: "#f8fafc", color: "#475569", lineHeight: 1.55, marginBottom: 8 },

  notCovered: { background: "#fff", border: "1px dashed #d8dee6", borderRadius: 16, padding: 20, marginTop: 20 },
  notCoveredHead: { marginBottom: 12 },
  notCoveredTitle: { fontSize: 14, fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.04em" },
  notCoveredSub: { display: "block", fontSize: 12.5, color: "#94a3b8", marginTop: 4 },
  notCoveredGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 20 },
  ncSubject: { fontSize: 12, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.05em", color: "#94a3b8", marginBottom: 6 },

  footer: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 28px", background: "#fff", borderTop: "1px solid #eaecef" },
  ghost: { border: "1px solid #d1d5db", background: "#fff", color: "#334155", padding: "10px 18px", borderRadius: 10, cursor: "pointer", fontSize: 13.5, fontWeight: 600 },
  primary: { border: "none", background: "#0f172a", color: "#fff", padding: "10px 20px", borderRadius: 10, cursor: "pointer", fontSize: 13.5, fontWeight: 700 },
};
